<template>
        <div v-if="order.orderId">
            <b-container fluid>
                <b-row v-if="printView" class="mb-5">
                    <b-col sm="9">
                        <h4>Order {{ order.orderId }} &ndash; ${{ order.orderTotal }}</h4>

                        <barcode v-if="printView" :data="order.orderId"></barcode>
                    </b-col>

                    <b-col align="right">
                        {{ moment((order.platform && order.platform.date) || order.createdAt).format('M/D/YYYY h:mma') }}
                        <div v-if="invoiceFlagsLine1 || invoiceFlagsLine2" class="mt-2 text-right font-weight-bold" style="font-size: 1.3rem;">
                            <div v-if="invoiceFlagsLine1">Office: {{ invoiceFlagsLine1 }}</div>
                            <div v-if="invoiceFlagsLine2">{{ invoiceFlagsLine2 }}</div>
                        </div>
                    </b-col>
                </b-row>

                <b-row>
                    <b-col md>
                        <customer :customer="order.customer" :printView="printView" @update="doUpdate('customer', $event)"></customer>

                        <payment-list
                            :email="order.customer.email"
                            :platform="order.platform"
                            :payments="order.payment"
                            :refunds="order.refunds"
                            :order-id="order.orderId || order.id"
                            :order="order"
                            :printView="printView"
                            class="mt-5"
                            @update="doUpdate('payment', $event)"
                            @refunds-updated="onRefundsUpdated"
                        ></payment-list>

                        <refund-list :refunds="order.refunds" :printView="printView" class="mt-5" @update="doUpdate('refunds', $event)"></refund-list>
                    </b-col>

                    <b-col md>
                        <platform :platform="order.platform" :printView="printView" :location="order.location" @update="doUpdate('platform', $event)"></platform>

                        <shipping :shipping="order.shipping" :printView="printView" :shippingTotal="order.shippingTotal" :orderId="order.orderId" :delivery="order.delivery" class="mt-5" @update="doUpdate('shipping', $event)"></shipping>

                        <note-list :notes="updated.notes && updated.notes.length > order.notes.length ? updated.notes : order.notes" :printView="printView" class="mt-5" @update="doUpdate('notes', $event)" :newlyCancelled="false"></note-list>

                        <discount-list :discounts="order.discounts" :printView="printView" @update="doUpdate('discounts', $event)" @updateDiscounts="updateDiscounts" @removeDiscount="removeDiscount" class="mt-5"></discount-list>
                    </b-col>
                </b-row>

                <b-row class="mt-5">
                    <b-col class="header-menu-text">
                        <h5 class="text-secondary">{{ numOrderItems }} {{ numOrderItems === 1 ? 'Item' : 'Items' }} - ${{ orderItemTotal }}</h5>
                    </b-col>
                </b-row>

                <div v-if="isOrderOpen && !printView">
                    <b-overlay :show="isSearching" variant="light" opacity="0.4">
                    <b-form-input :disabled="this.disableItemSearch || order.location === 'Cancelled'" :state="addItemState" size="sm" class="mb-3" :placeholder="searchPlaceholder" v-model="addedItem" v-on:keyup.enter="addItem"></b-form-input>
                    <b-form-invalid-feedback id="input-live-feedback">
                        {{ addItemStateText }}
                    </b-form-invalid-feedback>
                    </b-overlay>
                </div>

                <b-list-group
                        v-for="(item, index) in sortedItems"
                        v-bind:key="'orderitem-' + index">
                        
                    <b-row>
                       
                        <b-col class="py-3">
                            <item :item="item.details" :status="item.status || null" :printView="printView" @showImages="handleShowImages" :ref="'orderItem' + index" :orderDetailsInstance="this"></item>
                        </b-col>

                        <b-col v-if="!printView && isOrderOpen" sm="1" align="right">
                            <b-dropdown :disabled="item.status && (item.status.type === 'removed' || item.status.type === 'cancelled')" ref="removeItemDropdown" no-caret size="sm" variant="link" class="menu-btn" toggle-class="text-decoration-none">
                                <template #button-content>
                                    <b-button variant="outline-secondary" size="sm">
                                        <b-icon-x></b-icon-x>
                                    </b-button>
                                </template>

                                <remove-item :item="item" :orderId="order.orderId" @removeItem="removeOrderItem(index, $event)"></remove-item>
                            </b-dropdown>
                        </b-col>
                    </b-row>

                </b-list-group>
            </b-container>

            <b-modal v-if="!printView" id="addItemModalOrderDetails"
                     size="lg"
                     scrollable
                     hide-footer
                     button-size="sm"
                     centered>

                <template #modal-header>
                    <h4>Search results</h4>
                </template>

                <b-list-group>
                    <b-list-group-item v-for="(item, index) in potentialItems" :key="index">
                        <edit-order-item :editing='editingItem' :orderId='order.orderId' :existingOrderQuantity='itemQuantityOnOrder(item)' :orderItems='order.items' :item="item" @addOrderItem="$event => addOrderItem($event)" @toggleEditing="toggleEditingItem"></edit-order-item>
                        <b-list-group-item v-if="item.listing.offsite" variant="warning" size="sm" class="mt-3">This item can't be added because it is off-site.</b-list-group-item>
                    </b-list-group-item>
                </b-list-group>
            </b-modal>

            <b-modal v-if="!printView" centered
                     size="lg"
                     id="orderItemImageModal"
                     hide-footer
                     hide-header
                     ref="imageModal"
                     v-model="showImageModal">
                <div v-if="modalImages && modalImages.length">
                    <b-row>
                        <b-col sm="9">
                            <div>
                                <b-img :src="currentImageUrl"
                                       @click="openImageInNewWindow(currentImageUrl)"
                                       class="order-item-image-main"
                                       fluid>
                                </b-img>
                            </div>
                        </b-col>
                        <b-col align="center">
                            <b-link v-for="(img, index) in modalImages"
                                    @click="viewImage(index)"
                                    :key="index">
                                <b-img width="125px;"
                                       class="mr-2"
                                       thumbnail
                                       block
                                       :src="getImageUrl(img)">
                                </b-img>
                            </b-link>
                        </b-col>
                    </b-row>
                </div>
            </b-modal>

        </div>
</template>

<script>
    import Platform from "./Platform";
    import NoteList from "./NoteList";
    import PaymentList from "./PaymentList";
    import RefundList from "./RefundList";
    import Shipping from "./Shipping";
    import Customer from "./Customer";
    import EditOrderItem from "./EditOrderItem";
    import Item from './Item';
    import RemoveItem from "./RemoveItem";
    import DiscountList from "./DiscountList";
    import ordersApi from "../../api/orders";
    import inventoryApi from "../../api/inventory";
    import Barcode from "../Barcode";

    export default {
        name: "OrderDetails",
        components: { Customer, Shipping, Platform, NoteList, PaymentList, RefundList, EditOrderItem, Item, RemoveItem, DiscountList, Barcode },
        mixins: [ ordersApi, inventoryApi ],
        props: [ 'order', 'printView', 'updated', 'disableItemSearch'],
        data() {
            return {
                addedItem: null,
                addItemState: null,
                addItemStateText: 'No item found.',
                potentialItems: [],
                isSearching: false,
                editingItem: false,
                sortedItems: [],
                showImageModal: false,
                modalImages: [],
                currentImageIndex: 0,
            }
        },
        methods: {
            doUpdate(key, obj, isArray = false) {
                let updateObj = {};

                if (isArray) {
                    if (!Array.isArray(updateObj[key])) {
                        updateObj[key] = [];
                    }

                    updateObj[key].push(obj);
                } else {
                    updateObj[key] = obj;
                }

                this.$emit('updateOrderDetails', updateObj);
            },
            updateDiscounts(newDiscounts) {
                this.$set(this.order, 'discounts', newDiscounts);
                let newTotal = this.orderItemTotal - this.orderDiscountTotal + (this.order.shipping.cost || this.order.shippingTotal);
                this.$set(this.order, 'orderTotal', newTotal);
            },
            removeDiscount(amount) {
                let newTotal = this.order.orderTotal + parseFloat(amount);
                this.$set(this.order, 'orderTotal', newTotal);
            },
            addShipment(tracking) {
                const shipmentObj = {
                    username: this.$auth.user.nickname,
                    tracking: tracking,
                };

                if (!this.order.shipping.shipments) {
                    this.order.shipping.shipments = [];
                }

                this.order.shipping.shipments.push(shipmentObj);

                this.doUpdate('shipping', this.order.shipping);

                this.$refs.trackingDropdown.hide();
            },
            async addOrderItem(updatedItem) {
                this.$emit('addItemToOrder', updatedItem);
                this.editingItem = false;
                this.$bvModal.hide('addItemModal');
            },
            sortByLocation(property, nested, obj = 'details', childObj = 'listing') {
                return function (objA, objB) {
                    let locA, locB;

                    if (nested && nested.length) {
                        locA = objA[nested][0][obj][childObj][property];
                        locB = objB[nested][0][obj][childObj][property];

                    } else {
                        locA = objA[obj][childObj][property];
                        locB = objB[obj][childObj][property];
                    }

                    if (!locA) {
                        return -1;
                    }

                    if (!locB) {
                        return 1;
                    }

                    // Convert to lowercase
                    locA = locA.toLowerCase();
                    locB = locB.toLowerCase();

                    const splitA = locA.match(/[a-zA-Z]+|[0-9]+/g);
                    const splitB = locB.match(/[a-zA-Z]+|[0-9]+/g);

                    if (locA.length > locB.length) {
                        return 1;
                    } else if (locA.length < locB.length) {
                        return -1;
                    }

                    if (splitA[0] === splitB[0]) {
                        if (splitA[1] > splitB[1]) {
                            return 1;
                        } else if (splitA[1] < splitB[1]) {
                            return -1;
                        } else {
                            if (!splitA[2]) {
                                return 0;
                            }

                            if (splitA[2] > splitB[2]) {
                                return 1;
                            } else if (splitA[2] < splitB[2]) {
                                return -1;
                            } else {
                                if (!splitA[3]) {
                                    return 0;
                                }

                                if (splitA[3] > splitB[3]) {
                                    return 1;
                                } else if (splitA[3] < splitB[3]) {
                                    return -1;
                                }

                                return 0;
                            }
                        }
                    } else {
                        if (splitA[0] > splitB[0]) {
                            return 1;
                        } else if (splitA[0] < splitB[0]) {
                            return -1;
                        }
                    }
                }
            },

            async removeOrderItem(index, updatedItem) {
                this.$emit('removeItemFromOrder', updatedItem);
                this.$refs.removeItemDropdown[index].hide();
            },
            getActiveListings(item) {
                let newItems = [];

                item.listing.forEach(listing => {
                    if (listing.type === 'active' && listing.count > 0) {
                        let newItem = {...item};
                        newItem.listing = listing;
                        newItems.push(newItem);
                    }
                });

                return newItems;
            },
            async addItem() {
                this.addItemState = null;
                this.addItemStateText = '';
                this.potentialItems = [];
                this.isSearching = true;

                if (!isNaN(this.addedItem)) {
                    try {
                        const item = await this.getItem(this.addedItem);
                        
                        let actives = this.getActiveListings(item);

                        if (actives.length > 0) {
                            actives.forEach(newItem => {
                                newItem.quantity = 1;

                                this.potentialItems.push(newItem);
                            });

                            this.$bvModal.show('addItemModal');
                        } else {
                            this.addItemState = false;
                            this.addItemStateText = 'Item not in stock.';
                        }

                        this.isSearching = false;
                    } catch(err) {
                        if (err.response && err.response.status && err.response.status === 404) {
                            this.addItemState = false;
                            this.addItemStateText = 'Item does not exist.';
                        }

                        this.isSearching = false;
                    }

                } else {
                    try {
                        let results = await this.getItems({ q: this.addedItem });
                        results.docs.forEach(item => {
                            if (item.listing.type === 'active' && item.listing.count > 0 && this.itemNotOnOrder(item)) {
                                item.quantity = 1;

                                this.potentialItems.push(item);
                            }
                        });

                        if (!this.potentialItems.length) {
                            this.addItemState = false;
                            this.addItemStateText = 'No items found.';
                        } else {
                            this.$bvModal.show('addItemModal');
                        }

                        this.isSearching = false;
                    } catch (err) {
                        this.addItemState = false;
                        this.addItemStateText = 'No items found.';

                        this.isSearching = false;
                    }
                }
                // if a number, and doesn't have an underscore assume it's the first active listing
            },
            itemNotOnOrder(item) {
                let notOnOrder = true;

                if (this.order.items.length > 0) { this.order.items.forEach(i => {
                    if ((i.status && i.status.type === 'removed') && i.details.listing._id === item.listing._id) {
                        notOnOrder = false;
                    }
                }); }

                return notOnOrder;
            },
            itemQuantityOnOrder(potentialItem) {
                if (this.order.items.length > 0) {
                    let itemOnOrder = this.order.items.filter((i) => {
                        return i.details.listing._id === potentialItem.listing._id  
                    });
                    if (itemOnOrder.length > 0 && itemOnOrder[0].details.quantity > 0) {
                        if (itemOnOrder[0].status && (itemOnOrder[0].status.type === "removed" || "cancelled")) return 0
                        return itemOnOrder[0].details.quantity
                    }
                    return 0;
                }
                return 0; 
            },
            toggleEditingItem() {
                this.editingItem = !this.editingItem;
            },
            sortItems(items) {
                return items.sort((a, b) => {
                    // Check if the properties exist before accessing them
                    const aSize = a.details && a.details.release && a.details?.release?.media[0]?.size || '';
                    const bSize = b.details && b.details.release && b.details?.release?.media[0]?.size || '';

                    const sizeOrder = ["12", "10", "7"];
                    const aSizeIndex = sizeOrder.includes(aSize) && aSize !== "7" ? 0 : sizeOrder.indexOf(aSize);
                    const bSizeIndex = sizeOrder.includes(bSize) && bSize !== "7" ? 0 : sizeOrder.indexOf(bSize);

                    if (aSizeIndex < bSizeIndex) {
                        return -1;
                    } else if (aSizeIndex > bSizeIndex) {
                        return 1;
                    } else {
                        // Sizes are equal, sort by location
                        const locationComparison = this.sortByLocation('location')(a, b);
                        if (locationComparison !== 0) {
                            return locationComparison;
                        } else {
                            // Locations are equal, sort by label name
                            let aLabelName = a.details && a.details.release && a.details.release.label && a.details.release.label.name;
                            let bLabelName = b.details && b.details.release && b.details.release.label && b.details.release.label.name;

                            if (!aLabelName) {
                                return -1;
                            }
                            if (!bLabelName) {
                                return 1;
                            }

                            aLabelName = aLabelName.toLowerCase();
                            bLabelName = bLabelName.toLowerCase();

                            if (aLabelName < bLabelName) {
                                return -1;
                            } else if (aLabelName > bLabelName) {
                                return 1;
                            } else {
                            // Label names are equal, sort by catalog number
                                const aCatalogNo = a.details && a.details.release && a.details.release.label && a.details.release.label.catalogNo;
                                const bCatalogNo = b.details && b.details.release && b.details.release.label && b.details.release.label.catalogNo;
                            if (aCatalogNo < bCatalogNo) {
                                return -1;
                            } else if (aCatalogNo > bCatalogNo) {
                                return 1;
                            } else {
                                return 0;
                            }
                        }
                    }
                    }
                });
            },
            handleShowImages(images) {
                console.log('handleShowImages called with:', images);
                this.showImages(images);
            },
            showImages(images) {
                console.log('showImages called with:', images);
                if (!images || !images.length) {
                    console.log('No images, returning');
                    return;
                }
                this.modalImages = images;
                this.currentImageIndex = 0;
                this.showImageModal = true;
                console.log('showImageModal set to:', this.showImageModal);
                console.log('modalImages:', this.modalImages);
                this.$nextTick(() => {
                    console.log('Calling $bvModal.show');
                    this.$bvModal.show('orderItemImageModal');
                    console.log('$bvModal.show called');
                });
            },
            getImageUrl(img) {
                return process.env.VUE_APP_IMAGES_URL + img.url;
            },
            get currentImageUrl() {
                if (this.modalImages && this.modalImages.length > 0 && this.modalImages[this.currentImageIndex]) {
                    return this.getImageUrl(this.modalImages[this.currentImageIndex]);
                }
                return '';
            },
            viewImage(index) {
                this.currentImageIndex = index;
            },
            openImageInNewWindow(imageUrl) {
                window.open(imageUrl, 'imageViewer');
            },
            onRefundsUpdated(refunds) {
                this.$set(this.order, 'refunds', refunds || []);
            },
            resetRefundState() {
                this.refundPayment = null;
                this.refundAmount = null;
                this.refundNote = '';
                this.maxRefundAmount = 0;
                this.totalRefundedForPayment = 0;
                this.isSubmittingRefund = false;
                this.refundError = null;
            },
            async confirmRefund() {
                if (!this.refundPayment) {
                    return;
                }

                const amount = Number(this.refundAmount);

                if (!amount || amount <= 0) {
                    this.refundError = 'Refund amount must be greater than 0.';
                    return;
                }

                if (this.maxRefundAmount > 0 && amount - this.maxRefundAmount > 0.009) {
                    this.refundError = `Refund amount cannot exceed $${this.maxRefundAmount.toFixed(2)}.`;
                    return;
                }

                const confirmed = await this.$bvModal.msgBoxConfirm(
                    `Are you sure you want to refund $${amount.toFixed(2)} to the customer?`,
                    {
                        title: 'Confirm Refund',
                        size: 'md',
                        okVariant: 'danger',
                        okTitle: 'Yes, refund',
                        cancelTitle: 'Cancel',
                        footerClass: 'p-2',
                        centered: true,
                    }
                );

                if (!confirmed) {
                    return;
                }

                this.isSubmittingRefund = true;
                this.refundError = null;

                try {
                    const isPayPal = this.refundPayment.method === 'PayPal';
                    const payload = {
                        amount,
                        note: this.refundNote || undefined,
                        paymentTransactionId: this.refundPayment.transactionId,
                        reason: 'Order refund',
                        type: isPayPal ? 'paypal' : 'stripe',
                    };

                    if (!isPayPal) {
                        payload.chargeId = this.refundPayment.transactionId;
                    }

                    const updatedOrder = await this.refundOrder(this.order.orderId, payload);

                    if (updatedOrder && Array.isArray(updatedOrder.refunds)) {
                        this.$set(this.order, 'refunds', updatedOrder.refunds);
                    }

                    this.$bvModal.hide('refundPaymentModal');
                } catch (err) {
                    this.refundError = 'There was a problem processing the refund. Please try again.';
                    // eslint-disable-next-line no-console
                    console.error('Error processing refund', err);
                } finally {
                    this.isSubmittingRefund = false;
                }
            }
            
        },
        computed: {
            numOrderItems() {
                let count = 0;

                if (this.order.items.length > 0) {
                    this.order.items.forEach(item => {
                        if (!item.status || item.status.type !== 'removed') {
                            count += parseInt(item.details.quantity);
                        }
                    });
                }

                return count;
            },
            orderItemTotal() {
                let total = 0;
                if (this.order.items.length > 0) {
                    this.order.items.forEach(item => {
                        if (!item.status || item.status.type !== 'removed') {
                            total += parseFloat(item.details.listing.price * item.details.quantity);
                        }
                    });
                }
                return total;
            },
            orderDiscountTotal() {
                let total = 0;
                this.order.discounts && this.order.discounts.length > 0 && this.order.discounts.forEach(item => {
                    total += parseFloat(item.amount)
                });
                return total;
            },
            isOrderOpen() {
                let notOpen = [ 'Cancelled', 'Delivered', 'Returned', 'In-Transit' ];

                return !this.order.location || this.order.location.indexOf(notOpen) === -1;
            },
            isCancelled() {
                if (this.updated.location) {
                    return this.updated.location && this.updated.location === "Cancelled";
                } else {
                    return false;
                }
            },
            searchPlaceholder() {
                if (this.disableItemSearch) {
                    return "Save previously cancelled order to add items..."
                } else if (this.order.location === "Cancelled") {
                    return "Cannot add items to a cancelled order..."
                } else {
                    return "Add item by entering SKU or search text..."
                }
            },
            // Invoice flags: C P # T G (line 1), Q I (line 2)
            invoiceFlagsLine1() {
                const f = this.order.flags || {};
                const parts = [];
                // C: Combined orders (show when NOT unmerged)
                if (f.unmerged) parts.push('C');
                // P: No payment and platform Web, Discogs, CDandLP
                const noPayment = !this.order.payment || this.order.payment.length === 0;
                const site = this.order.platform?.site || '';
                const pPlatform = ['Web', 'Discogs', 'CDandLP', 'Phone', 'Email', 'Walk-In', 'Post', 'Employee'].includes(site);
                if (noPayment && pPlatform) parts.push('P');
                // #: Phone number flag
                if (f.noPhone) parts.push('#');
                // T: Tax ID
                if (f.noLatinTaxId) parts.push('T');
                // G: General flag with custom text
                if (f.other && String(f.other).trim()) parts.push('G');
                return parts.join(', ');
            },
            invoiceFlagsLine2() {
                const parts = [];
                // I: International
                const country = this.order.customer?.address?.country;
                if (country && country !== 'US') parts.push('I');
                // Q: Queue - $100+, Priority Shipping, or Amazon
                // add up the item total from the order.items
                const total = this.order.items.reduce((acc, item) => acc + (item.details.listing.price * item.details.quantity), 0);
                const method = (this.order.shipping?.method || '').toLowerCase();
                const isPriority = method.includes('priority');
                const isAmazon = (this.order.platform?.site || '') === 'Amazon';
                if (total >= 100 || isPriority || isAmazon) parts.push('Q');

                return parts.join(' ');
            },
        },
        watch: {
        'order.items': {
            immediate: true,
            handler(newVal) {
                if (Array.isArray(newVal) && newVal.length > 1) {
                    // Use the sortByLocation method to sort the new value of order.items
                    this.sortedItems = this.sortItems([...newVal]);
                    // sort the items first by size, then by location, then by label then by label number
                } else {
                    // If the array has one or no items, just assign it to sortedItems
                    this.sortedItems = newVal;
                }
            },

        },
        },
    }
</script>

<style scoped>
    .no-click {
        cursor: default;
    }
    .order-item-image-main {
        cursor: pointer;
    }
    .order-item-image-main:hover {
        opacity: 0.8;
    }
</style>